import { Calendar, Building2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const dayNames = {
  en: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
  cz: ["Neděle", "Pondělí", "Úterý", "Středa", "Čtvrtek", "Pátek", "Sobota"],
};

function formatDate(dateStr, time, lang, months) {
  const d = new Date(dateStr);
  const day = dayNames[lang][d.getDay()];
  const dayNum = d.getDate();
  const month = months[d.getMonth()];
  const year = d.getFullYear();
  const timeFmt = time ? ` ${time}` : "";
  if (lang === "en") {
    return `${day}, ${month} ${dayNum}, ${year}${timeFmt}`;
  }
  return `${day}, ${dayNum}. ${month} ${year}${timeFmt}`;
}

export default function EventCard({ event }) {
  const { t, lang } = useLang();

  const handleClick = () => {
    if (event.soldOut) return;
    if (typeof window !== "undefined" && event.ticketUrl) {
      window.open(event.ticketUrl, "_blank", "noopener");
    }
  };

  const formattedDate = formatDate(event.date, event.time, lang, t.months);

  return (
    <div
      className="group cursor-pointer flex flex-col"
      onClick={handleClick}
      data-testid={`event-card-${event.id}`}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter") handleClick();
      }}
    >
      {/* Poster image */}
      <div className="relative overflow-hidden rounded-2xl border border-white/8 aspect-[3/4] bg-[#0c0c0e] transition-all duration-500 group-hover:border-purple-400/50 group-hover:shadow-[0_30px_60px_-20px_rgba(168,85,247,0.5)]">
        <img
          src={event.image}
          alt={event.title}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
          loading="lazy"
        />

        {/* Category pill */}
        <div className="absolute top-3 left-3 z-10">
          <span
            className="inline-flex items-center gap-1.5 bg-red-500/95 backdrop-blur-md text-white text-[11px] font-display font-semibold tracking-wide px-3 py-1.5 rounded-full"
            data-testid={`event-category-${event.id}`}
          >
            {event.category}
          </span>
        </div>

        {/* Sold-out chip (only when applicable) */}
        {event.soldOut && (
          <div className="absolute top-3 right-3 z-10">
            <span
              className="bg-black/70 backdrop-blur-md text-white text-[10px] font-display font-bold tracking-[0.2em] px-3 py-1.5 rounded-full border border-white/20"
              data-testid={`event-soldout-${event.id}`}
            >
              {t.eventCard.soldOut}
            </span>
          </div>
        )}
      </div>

      {/* Info below poster */}
      <div className="mt-4 px-1">
        <h3
          className="font-display font-bold uppercase text-white text-base md:text-lg tracking-tight leading-snug"
          data-testid={`event-title-${event.id}`}
        >
          {event.title}
        </h3>

        <div className="mt-2 flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 text-white/85 text-sm font-body min-w-0">
            <Calendar className="w-4 h-4 flex-shrink-0 text-red-400" />
            <span className="truncate">{formattedDate}</span>
          </div>
          <div className="flex items-center gap-1.5 text-white/80 text-sm font-body flex-shrink-0">
            <Building2 className="w-4 h-4 text-red-400" />
            <span>{event.city}</span>
          </div>
        </div>

        {event.address && (
          <p className="mt-1.5 text-white/45 text-xs font-body leading-relaxed">
            {event.address}
          </p>
        )}
      </div>
    </div>
  );
}
